import module from "module";
import _ from "lodash";
import {addAccountLabels} from 'components/general-ledger/common/gl.utils';

const templateUrl = require('./funds-movement.template.html');
module.component('fundsMovement', {
  templateUrl: templateUrl,
  controller: function ($filter, $routeParams, $timeout, glMappingsService, actionCommand, http,
                        actionCategoryCache, systemPropertyCache, branchCurrencyCache, authentication, popup,
                        denominationSerialNumbersService) {

    let that = this;
    const actionType = _.get($routeParams, 'actionType');
    const cashInActionCodes = ['CASH_IN', 'ADJUSTMENT_IN', 'FOREX_CASH_IN'];
    const forexActionCodes = ['FOREX_CASH_IN', 'FOREX_CASH_OUT'];
    const actionCodesWithDenomination = ['CASH_IN', 'CASH_OUT', 'ADJUSTMENT_IN', 'ADJUSTMENT_OUT', 'FOREX_CASH_IN', 'FOREX_CASH_OUT'];

    that.actionCode = actionType.toUpperCase().replace(/-/g, '_');

    that.actionName = $filter("prettyEnum")(that.actionCode);
    that.useDenomination = actionCodesWithDenomination.includes(that.actionCode);
    that.isForexAction = forexActionCodes.includes(that.actionCode);
    that.group = cashInActionCodes.includes(that.actionCode) ? 'INCOMING' : 'OUTGOING';
    that.entryType = ['FOREX_CASH_IN', 'CASH_IN', 'ADJUSTMENT_IN'].includes(that.actionCode) ? 'DEBIT' : 'CREDIT';
    that.fixedAccountLabel = that.useDenomination ? 'User cash' : 'User check';
    that.transaction = {units: [], ledgerAmount: 0};
    that.blockedUnits = [];
    that.categories = [];
    that.remarksRequired = false;
    that.fixedAccount = false;
    that.ledgerAccounts = [];
    that.currencies = [];
    that.requireRecalculate = true;

    const ledgerAccountSub = glMappingsService.accounts.toObservable().subscribe(ledgerAccounts => {
      // Apply labels to accounts applicable for
      that.ledgerAccounts = addAccountLabels(ledgerAccounts);
    });

    const systemPropertiesSub = systemPropertyCache.toObservable().subscribe(properties => {
      const ledgerSupportProperty = _.find(properties, {code: 'LEDGER_SUPPORT'});
      const currencyProp = properties.find(prop => prop.code === 'MAIN_CURRENCY_ISO_CODE');

      that.supportsLedger = ledgerSupportProperty && ledgerSupportProperty.value === 'TRUE';
      that.mainCurrencyIso = currencyProp.value;
      that.canSaveNoteSerialNumber = _.find(properties, {code: 'FOREIGN_DENOMINATION_SERIAL_NUMBERS_ENABLED'}).value === 'TRUE';
    });

    // On init set form as submitted to highlight invalid fields
    $timeout(() => {
      that.transactionForm.$setSubmitted();
    });

    const categoriesSub = actionCategoryCache.toObservable().subscribe(categories => {
      that.categories = categories.filter(cat => cat.actionType === that.actionCode);
    });

    that.onCategoryChange = () => {
      requireRecalculate();
      that.remarksRequired = false;
      if (that.transaction.categoryId) {
        const category = _.find(that.categories, {id: that.transaction.categoryId});

        if (category.supportedCurrencyIds == null) {
          setDefaultForeignCurrencies();
        } else {
          that.foreignCurrencies = that.currencies
            .filter(currency => currency.isoCode !== that.mainCurrencyIso && category.supportedCurrencyIds.find(id => id === currency.id));
        }

        if (category.ledgerAccountFullCode) {
          let fixedAccount = _.find(that.ledgerAccounts, {fullCode: category.ledgerAccountFullCode});
          that.blockedUnits = [
            {
              accountCode: fixedAccount.fullCode,
              amount: that.transaction.amount,
              entryType: that.entryType === 'CREDIT' ? 'DEBIT' : 'CREDIT'
            }
          ];
        } else {
          that.blockedUnits = [];
        }
        that.remarksRequired = category && category.remarksRequired;
      }
    };

    that.cancelChanges = () => {
      actionCommand.cancelChanges();
    };

    that.onCurrencyChange = () => {
      requireRecalculate();
      denominationSerialNumbersService.resetSerialNumbers(that.currency);
      that.currency = that.foreignCurrencies.find(cur => cur.id === that.currencyId);
      that.transaction.currencyId = that.currencyId;
    };

    that.denominationIsValid = () => {
      return !that.useDenomination || (that.transaction.denominationBundle != null && that.transaction.denominationValid);
    };

    that.save = () => {
      denominationSerialNumbersService.appendSerialNumbers(that.currency, that.transaction);
      that.request = {...that.transaction};

      if (this.isForexAction) {
        that.request.amount = that.transaction.ledgerAmount;
        that.request.amountInCurrency = that.transaction.amount;
        that.request.foreignCurrencyDenominationBundle = that.transaction.denominationBundle
        that.request.denominationBundle = null;
      }

      actionCommand.executeAndShowValidation(that.actionCode, that.request);
    };

    that.isDenominationReady = () => {
      if (that.isForexAction) {
        return that.currencyId != null;
      }
      return true;
    }

    that.isLedgerUnitsReady = () => {
      if (!that.supportsLedger) {
        return false;
      }

      if (that.isForexAction) {
        return !that.requireRecalculate;
      }

      return true;
    }

    that.onAmountChange = () => {
      if (!that.isForexAction) {
        updateLedger(that.transaction.amount);
      }
      requireRecalculate();
    }

    that.calculateExchangeAmount = async () => {
      const amountInSystemCurrency = await http.post("/forex/system-exchange/calculate", that.transaction.denominationBundle).toPromise();
      updateLedger(amountInSystemCurrency);
      that.requireRecalculate = false;
    }

    const setDefaultForeignCurrencies = () => {
      that.foreignCurrencies = that.currencies.filter(c => c.isoCode !== that.mainCurrencyIso);
    }

    that.$onInit = async () => {
      that.amountInputOrder = that.isForexAction ? 3 : 2;
      await fetchCurrencies();
    };

    const fetchCurrencies = async () => {
      const currencies = await branchCurrencyCache.withParam(Number(authentication.context.branchId)).toPromise();
      that.currencies = currencies;

      // initially, when no currency is selected the display for currency will be the main currency isoCode by default
      that.currency = currencies.find(cur => cur.isoCode === that.mainCurrencyIso);
      setDefaultForeignCurrencies();
    };

    const requireRecalculate = () => {
      that.requireRecalculate = true;
    }

    // Updates the first entry in the ledger to the inputted amount.
    const updateLedger = (amount) => {
      if (!that.supportsLedger) {
        return;
      }

      if (that.blockedUnits && that.blockedUnits.length) {
        that.blockedUnits[0].amount = amount;
      } else if (that.transaction.units && that.transaction.units.length >= 2) {
        that.transaction.units[1].amount = amount;
      }
      that.transaction.ledgerAmount = amount;
    }

    that.$onDestroy = () => {
      systemPropertiesSub.unsubscribe();
      categoriesSub.unsubscribe();
      ledgerAccountSub.unsubscribe();
    };
  }
});
